import { ComponentProps, forwardRef, useState } from "react";
import { styled } from "@mui/material/styles";
import { motion, HTMLMotionProps, AnimatePresence } from "framer-motion";
import Loading from "../Loading";
import {
  AnalyticsTrackingEvent,
  ArrowRightIcon,
  hexadecimal,
  MOBILE_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { useTrackActions } from "../../utils/hooks/useTracking";
import { useAppSelector } from "../../redux/hooks";

export interface TrackedMotionButtonProps extends HTMLMotionProps<"button"> {
  trackedEventName?: AnalyticsTrackingEvent;
  trackedAdditionalData?: Record<string, any>;
}
export interface TrackedSolidButtonProps
  extends ComponentProps<typeof SolidButton> {
  trackedEventName?: AnalyticsTrackingEvent;
  trackedAdditionalData?: Record<string, any>;
  disableMaxWidth?: boolean;
}
export interface TrackedOutlineButtonProps
  extends ComponentProps<typeof SolidButton> {
  trackedEventName?: AnalyticsTrackingEvent;
  trackedAdditionalData?: Record<string, any>;
  disableMaxWidth?: boolean;
}

export const TrackedMotionButton = forwardRef<
  HTMLButtonElement,
  TrackedMotionButtonProps
>(({ trackedEventName, trackedAdditionalData, onClick, ...props }, ref) => {
  const [trackAction] = useTrackActions();
  const domainConfig = useAppSelector((state) => state.appConfig?.domainConfig);
  const mainEntity = useAppSelector((state) => state.mainEntity);
  const brandEntity = useAppSelector((state) => state.brandEntity);

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    if (trackedEventName) {
      trackAction(trackedEventName, {
        brandName: mainEntity?.entity.brandName,
        brandId: mainEntity?.entity.brandId,
        brandclubId: brandEntity?.brandclub?.brandclubId,
        appId: domainConfig?.domainName,
        ...trackedAdditionalData,
      });
    }
    if (onClick) {
      onClick(event);
    }
  };

  return <motion.button ref={ref} onClick={handleClick} {...props} />;
});

TrackedMotionButton.displayName = "TrackedMotionButton";

export const ButtonLoadingEllipsis = ({
  baseFontSize = 12,
  blue,
  customColor,
  style,
}: {
  baseFontSize?: number;
  blue?: boolean;
  customColor?: string;
  style?: React.CSSProperties;
}) => {
  const ellipsisContainerSize = baseFontSize * 1.4;
  const ellipsisSize = baseFontSize * 2.6;
  const yOffset = ellipsisSize / 10;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        width: ellipsisContainerSize,
        height: ellipsisContainerSize,
        minWidth: ellipsisContainerSize,
        minHeight: ellipsisContainerSize,
        ...style,
      }}
    >
      <div
        style={{
          position: "absolute",
          width: ellipsisSize,
          height: ellipsisSize,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: `translateY(${yOffset}px)`,
        }}
      >
        <Loading
          ellipsis={!blue}
          ellipsisBlue={blue}
          size={ellipsisSize}
          ellipsisCustomColor={customColor}
        />
      </div>
    </div>
  );
};

export const SolidButton = styled(TrackedMotionButton)<{
  disableMaxWidth?: boolean;
}>(({ theme, disableMaxWidth }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  height: 50,
  fontSize: 16,

  borderRadius: theme.globalButtonRadius ?? 999,
  transition: "transform 0.3s",
  "&:active": {
    "&:not([disabled])": {
      transform: "scale(0.95)",
    },
  },
  "not(:focus-visible)": {
    outline: "none",
  },
  border: `1.5px solid ${
    theme.componentStyleOverrides?.bagDrawerCheckoutButton?.backgroundColor ||
    theme.palette.primary.main
  }`,
  backgroundColor:
    theme.componentStyleOverrides?.bagDrawerCheckoutButton?.backgroundColor ||
    theme.palette.primary.main,
  color:
    theme.componentStyleOverrides?.bagDrawerCheckoutButton?.textColor ||
    theme.palette.getContrastText(theme.palette.primary.main),
  padding: "7px 17px",

  ":disabled": {
    cursor: "not-allowed",
    opacity: 1,
    borderColor: "#dcdddc",
    backgroundColor: "#fff",
    color: "#dcdddc",
  },
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: 14,
    height: 44,
    ...(!disableMaxWidth && { maxWidth: 333 }),
  },
  fontWeight: 600,

  ".left_icon": {
    marginRight: 6,
  },
  ".button_img": {
    marginRight: 6,
    maxWidth: 18,
    maxHeight: 18,
  },
}));

export const OutlineButton = styled(TrackedMotionButton)<{
  disableMaxWidth?: boolean;
}>(({ theme, disableMaxWidth }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  height: 50,
  position: "relative",

  borderRadius: theme.globalButtonRadius ?? 999,
  transition: "transform 0.3s",
  "&:active": {
    "&:not([disabled])": {
      transform: "scale(0.95)",
    },
  },
  "not(:focus-visible)": {
    outline: "none",
  },
  border: `1.5px solid ${
    theme.componentStyleOverrides?.bagDrawerCheckoutButton?.backgroundColor ||
    theme.palette.primary.main
  }`,
  backgroundColor: "#fff",
  color:
    theme.componentStyleOverrides?.bagDrawerCheckoutButton?.backgroundColor ||
    theme.palette.primary.main,
  padding: "7px 17px",
  fontSize: 14,

  ":disabled": {
    opacity: 1,
    borderColor: "#dcdddc",
    backgroundColor: "#fff",
    color: hexadecimal("#000000")(30),
    cursor: "not-allowed",
  },
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: 14,
    height: 44,
    ...(!disableMaxWidth && { maxWidth: 333 }),
  },
  fontWeight: 600,

  ".button_img": {
    marginRight: 6,
    transform: "translateX(50%)",
    maxWidth: 18,
    maxHeight: 18,
  },
}));

export const NeutralButton = styled(SolidButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: "#f6f8fa",
  borderColor: "#f6f8fa",
}));

export const SolidButtonWithArrow = forwardRef<
  HTMLButtonElement,
  TrackedSolidButtonProps
>(({ ...props }, ref) => {
  const [hovered, setHovered] = useState(false);

  return (
    <SolidButton
      ref={ref}
      {...props}
      onMouseLeave={() => {
        setHovered(false);
      }}
      onMouseEnter={() => {
        setHovered(true);
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          style={{
            display: "flex",
            alignItems: "center",
          }}
          key={hovered ? "arrow" : "content"}
          initial={{ x: -30, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 30, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {hovered ? (
            <ArrowRightIcon
              style={{
                marginLeft: 0,
                width: 20,
                height: 20,
              }}
            />
          ) : (
            props.children
          )}
        </motion.div>
      </AnimatePresence>
    </SolidButton>
  );
});

export const OutlineButtonWithArrow = forwardRef<
  HTMLButtonElement,
  TrackedOutlineButtonProps
>(({ ...props }, ref) => {
  const [hovered, setHovered] = useState(false);
  const arrowAnimations = {
    hover: {
      x: ["-50%", "50%"],
      opacity: [0, 1],
      transition: {
        duration: 0.25,
        delay: 0.25,
      },
    },
    unhover: {
      x: ["50%", "150%"],
      opacity: [1, 0],
      transition: {
        duration: 0.25,
      },
    },
  };

  const textAnimations = {
    hover: {
      x: ["0%", "15%"],
      opacity: [1, 0],
      transition: {
        duration: 0.25,
      },
    },
    unhover: {
      x: ["-15%", "0%"],
      opacity: [0, 1],
      transition: {
        duration: 0.25,
        delay: 0.25,
      },
    },
  };

  return (
    <OutlineButton
      ref={ref}
      {...props}
      onMouseLeave={() => {
        setHovered(false);
      }}
      onMouseEnter={() => {
        setHovered(true);
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          position: "relative",
        }}
      >
        <motion.div
          style={{
            position: "absolute",
            right: "50%",
            width: 20,
            height: 20,
          }}
          animate={hovered ? "hover" : "unhover"}
          variants={arrowAnimations}
        >
          <ArrowRightIcon />
        </motion.div>

        <motion.div
          animate={hovered ? "hover" : "unhover"}
          variants={textAnimations}
        >
          {props.children}
        </motion.div>
      </div>
    </OutlineButton>
  );
});
